import axios from 'axios';
import CryptoJS from 'crypto-js';
import jwt from 'jsonwebtoken';

import { APP_KEY, API_CONF, API_ROUTES, PAGE_ROUTE } from '../constants';

const api = axios.create({
  ...(API_CONF.BASE_URL !== '' && { baseURL: API_CONF.BASE_URL }),
  timeout: API_CONF.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(function (config) {
  const token = sessionStorage.getItem('token');
  config.headers.Authorization = token ? `Bearer ${token}` : '';
  return config;
});

export default class API {
  static encryptedSessionStorage = class extends API {
    static getTokenData() {
      return jwt.decode(sessionStorage.getItem('token'));
    }
    static getItem(key, secret) {
      return CryptoJS.AES.decrypt(String(sessionStorage.getItem(key)), String(APP_KEY) + String(secret)).toString(CryptoJS.enc.Utf8);
    }
    static setItem(key, value, secret) {
      sessionStorage.setItem(key, CryptoJS.AES.encrypt(String(value), String(APP_KEY) + String(secret)).toString());
    }
  };

  static async list() {
    return await api
      .get(API_ROUTES.LIST, {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        // const date = new Date();
        // const timestamp = date.getTime();
        try {
          const date = new Date();
          const timestamp = date.toISOString().split('T')[0];
          const content = Buffer.from(response.data, 'ascii');
          const blob = new Blob([content], { type: 'text/csv; charset=windows-1252' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `banco-mais-clientes-${timestamp}.csv`;
          link.click();
        } catch (e) {
          throw e;
        }
      })
      .catch((e) => {
        // const error = { ...e, message: 'Você não tem autorização para baixar esse arquivo.' };
        const error = { ...e, message: e.message };
        throw error;
      });
  }

  static async signIn(data) {
    console.log(data);
    return await api
      .post(API_ROUTES.AUTH, data)
      .then((response) => {
        const decoded = jwt.decode(response.data.token);
        sessionStorage.setItem('email', response.data.email);
        sessionStorage.setItem('refreshToken', response.data.refreshToken);
        sessionStorage.setItem('token', response.data.token);
        API.encryptedSessionStorage.setItem('account_id', response.data.account_id, decoded.uid);
        API.encryptedSessionStorage.setItem('email', response.data.email, decoded.uid);
        API.encryptedSessionStorage.setItem('id', response.data.id, decoded.uid);
        API.encryptedSessionStorage.setItem('name', response.data.name, decoded.uid);
        API.encryptedSessionStorage.setItem('profile_id', response.data.profile_id, decoded.uid);
        let classe_ids = [];
        if (Array.isArray(response.data.classe)) {
          classe_ids = response.data.classe.map((classe) => classe.id);
        } else {
          console.log('Expected response.data.classe as array');
        }
        API.encryptedSessionStorage.setItem('classe_ids', JSON.stringify(classe_ids), decoded.uid);
        return response;
      })
      .catch((e) => {
        let error = e;
        let message = '';
        let status = 0;
        if (e.status) {
          status = e.status;
        }
        if (e.response) {
          error = e.response;
          status = parseInt(e.response.status);
        } else {
          status = 0;
        }
        if (error.data) {
          error = error.data;
        }
        switch (status) {
          case 0:
            message = 'Falha na comunicação com o sistema interno do Banco Mais. Tente novamente mais tarde';
            break;
          case 401:
            message = 'Login incorreto. Verifique seu login e sua senha.';
            break;
          default:
            message = 'Não foi possível logar no sistema do Banco Mais.';
            break;
        }
        error = { ...error, status, message };
        throw error;
      });
  }

  static async signOut() {
    const data = {
      token: sessionStorage.getItem('token'),
      refreshToken: sessionStorage.getItem('refreshToken'),
    };
    await api
      .post(API_ROUTES.LOGOUT, data)
      .then((response) => {
        return response;
      })
      .catch((e) => {
        console.log(e.toString());
      });
    sessionStorage.removeItem('account_id');
    sessionStorage.removeItem('classe_ids');
    sessionStorage.removeItem('email');
    sessionStorage.removeItem('id');
    sessionStorage.removeItem('name');
    sessionStorage.removeItem('profile_id');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('school_id');
    sessionStorage.removeItem('token');
  }

  static async handleException(exception, message) {
    const e = exception;
    if (e.response) {
      const response = e.response;
      if (response.data) {
        const data = response.data;
        if (data.error) {
          const error = data.error;
          console.error('Error:', error.message);
          throw error; // <- this enables error message detailed
        }
      }
      switch (response.status) {
        case 401:
          window.location.href = PAGE_ROUTE.SIGNIN;
          await API.signOut();
          break;
        default:
          break;
      }
    }
    const error = { ...exception, message };
    throw error;
  }

  static async refreshToken() {
    const data = {
      refreshToken: sessionStorage.getItem('refreshToken'),
    };
    try {
      const response = await api.post(API_ROUTES.REFRESH, data);
      if (response.data) {
        sessionStorage.setItem('token', response.data.token);
        sessionStorage.setItem('refreshToken', response.data.refreshToken);
        // console.log(`\n{ "token": "${response.data.token}", "refreshToken": "${response.data.refreshToken}",  }\n`);
      }
      return response;
    } catch (e) {
      console.log(e.response);
    }
  }

  static async index(route, error, query = null) {
    try {
      if (query) {
        const queryString = Object.keys(query)
          .map((key) => {
            let string = '';
            const value = query[key];
            if (Array.isArray(value)) {
              string = Object.keys(value)
                .map((item) => {
                  return `${key}=${value[item]}`;
                })
                .join('&');
            } else {
              string = `${key}=${value}`;
            }
            return `${string}`;
          })
          .join('&');
        return await api.get(`${route}${queryString ? '?' + queryString : ''}`);
      } else {
        return await api.get(route);
      }
    } catch (e) {
      await API.handleException(e, `Não foi possível listar ${error} disponiveis`);
    }
  }

  static async store(route, error, data) {
    try {
      return await api.post(route, data);
    } catch (e) {
      await API.handleException(e, `Não foi possível criar ${error}`);
    }
  }

  static async show(route, error, id) {
    try {
      return await api.get(`${route}/${id}`);
    } catch (e) {
      await API.handleException(e, `Não foi possível carregar as informações ${error}`);
    }
  }

  static async update(route, error, id, data) {
    try {
      return await api.put(`${route}/${id}`, data);
    } catch (e) {
      await API.handleException(e, `Não foi possível atualizar ${error}`);
    }
  }

  static async destroy(route, error, id) {
    try {
      return await api.delete(`${route}/${id}`);
    } catch (e) {
      await API.handleException(e, `Não foi possível remover ${error}`);
    }
  }

  static async hide(route, error, id, data) {
    try {
      return await api.put(`${route}/${id}`, data);
    } catch (e) {
      console.error(e);
      await API.handleException(e, `Não foi possível remover ${error}`);
    }
  }

  static async compare(route, error, id) {
    try {
      return await api.get(`${route}/${id}`);
    } catch (e) {
      console.error(e);
      await API.handleException(e, `Não foi possível comparar ${error}`);
    }
  }

  static Accounts = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.ACCOUNTS, 'as contas', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.ACCOUNTS, 'a conta', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.ACCOUNTS, 'da conta', id);
    }
    static async update(id, saldo) {
      return super.update(API_ROUTES.ACCOUNTS, 'a conta', id, { saldo });
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.ACCOUNTS, 'a conta', id);
    }
  };

  static Banks = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.BANKS, 'os bancos', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.BANKS, 'o banco', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.BANKS, 'do banco', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.BANKS, 'o banco', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.BANKS, 'o banco', id);
    }
  };

  static Schools = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.SCHOOLS, 'as agências', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.SCHOOLS, 'a agência', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.SCHOOLS, 'da agência', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.SCHOOLS, 'a agência', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.SCHOOLS, 'a agência', id);
    }
  };

  static Series = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.SERIES, 'as séries', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.SERIES, 'a série', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.SERIES, 'da série', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.SERIES, 'a série', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.SERIES, 'a série', id);
    }
  };

  static Classes = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.CLASSES, 'as classes', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.CLASSES, 'a classe', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.CLASSES, 'da classe', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.CLASSES, 'a classe', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.CLASSES, 'a classe', id);
    }
  };

  static Users = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.USERS, 'os clientes', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.USERS, 'o cliente, tente usar um email diferente', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.USERS, 'do cliente', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.USERS, 'o cliente', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.USERS, 'o cliente', id);
    }
    static async hide(id, data) {
      return super.hide(API_ROUTES.HIDE, 'o cliente', id, data);
    }
  };

  static Depositos = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.DEPOSITOS, 'os depósitos', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.DEPOSITOS, 'o depósito', data);
    }
    static async lote(data) {
      return super.store(API_ROUTES.DEPOSITOS_LOTE, 'o depósito', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.DEPOSITOS, 'do depósito', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.DEPOSITOS, 'o depósito', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.DEPOSITOS, 'o depósito', id);
    }
  };

  static Depositologs = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.DEPOSITOLOGS, 'os depósitos', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.DEPOSITOLOGS, 'o depósito', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.DEPOSITOLOGS, 'do depósito', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.DEPOSITOLOGS, 'o depósito', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.DEPOSITOLOGS, 'o depósito', id);
    }
  };

  static Profiles = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.PROFILES, 'os perfis', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.PROFILES, 'o perfil', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.PROFILES, 'do perfil', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.PROFILES, 'o perfil', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.PROFILES, 'o perfil', id);
    }
  };

  static Saques = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.SAQUES, 'os saques', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.SAQUES, 'o saque', data);
    }
    static async lote(data) {
      return super.store(API_ROUTES.SAQUES_LOTE, 'o saque', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.SAQUES, 'do saque', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.SAQUES, 'o saque', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.SAQUES, 'o saque', id);
    }
  };

  static Transferencias = class extends API {
    static async index(query) {
      return super.index(API_ROUTES.TRANSFERENCIAS, 'as transferências', query);
    }
    static async store(data) {
      return super.store(API_ROUTES.TRANSFERENCIAS, 'a transferência', data);
    }
    static async show(id) {
      return super.show(API_ROUTES.TRANSFERENCIAS, 'da transferência', id);
    }
    static async update(id, data) {
      return super.update(API_ROUTES.TRANSFERENCIAS, 'a transferência', id, data);
    }
    static async destroy(id) {
      return super.destroy(API_ROUTES.TRANSFERENCIAS, 'a transferência', id);
    }
  };

  static Balance = class extends API {
    static async show(id) {
      return super.show(API_ROUTES.BALANCE, 'da histórico', id);
    }

    static async compare(id) {
      return super.compare(API_ROUTES.COMPARAR, 'da comparação', id);
    }

  };
}
